import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  CheckCircle2,
  History,
  TrendingDown,
  Truck,
} from 'lucide-react'
import { Drawer } from '@/components/ui/Drawer'
import { Button } from '@/components/ui/Button'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { AuthContext } from '@/features/auth/AuthContext'
import {
  getMaterialStockPolicyApi,
  updateMaterialStockPolicyApi,
  type MaterialStockPolicyDetailDto,
  type ReplenishPolicy,
} from '@/services/materials/stockPolicyApiClient'
import { formatDate } from '@/lib/dateFormat'
import { listSuppliersApi } from '@/services/suppliers/apiClient'
import type { Supplier } from '@/services/suppliers/types'

export interface MaterialPropertiesPanelProps {
  isOpen: boolean
  materialId: string | null
  onClose: () => void
  onSaved?: () => void
}

export interface StockPolicyFormValues {
  minQty: string
  autoSuggest: boolean
  safetyStock: string
  reorderPoint: string
  maxQty: string
  preferredSupplierId: string
  leadTimeDays: string
}

export interface StockPolicyFieldErrors {
  minQty?: string
  maxQty?: string
  safetyStock?: string
  reorderPoint?: string
  leadTimeDays?: string
}

/**
 * Pure helper: Validate form inputs and produce localized Vietnamese errors.
 */
export function validateStockPolicyForm(values: StockPolicyFormValues): {
  isValid: boolean
  errors: StockPolicyFieldErrors
} {
  const errors: StockPolicyFieldErrors = {}

  const minNum = values.minQty.trim() === '' ? 0 : Number(values.minQty)
  if (isNaN(minNum) || minNum < 0) {
    errors.minQty = 'Tồn tối thiểu phải là số không âm'
  }

  if (values.autoSuggest && minNum <= 0) {
    errors.minQty = 'Cần đặt ngưỡng cảnh báo > 0 để tự động đề xuất mua'
  }

  const safetyNum = values.safetyStock.trim() === '' ? 0 : Number(values.safetyStock)
  if (isNaN(safetyNum) || safetyNum < 0) {
    errors.safetyStock = 'Tồn an toàn phải là số không âm'
  }

  const ropNum = values.reorderPoint.trim() === '' ? 0 : Number(values.reorderPoint)
  if (isNaN(ropNum) || ropNum < 0) {
    errors.reorderPoint = 'Điểm đặt hàng lại phải là số không âm'
  }

  if (values.maxQty.trim() !== '') {
    const maxNum = Number(values.maxQty)
    if (isNaN(maxNum) || maxNum < 0) {
      errors.maxQty = 'Tồn tối đa phải là số không âm'
    } else if (maxNum < minNum) {
      errors.maxQty = 'Tồn tối đa phải lớn hơn hoặc bằng tồn tối thiểu'
    }
  }

  if (values.leadTimeDays.trim() !== '') {
    const leadNum = Number(values.leadTimeDays)
    if (isNaN(leadNum) || !Number.isInteger(leadNum) || leadNum < 0) {
      errors.leadTimeDays = 'Thời gian giao hàng phải là số nguyên không âm'
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  }
}

/**
 * Material properties drawer panel.
 * Grouped cleanly: Overview, Stock status, Warning thresholds, Advanced settings, Recent purchase history, Shortcuts.
 */
export function MaterialPropertiesPanel({
  isOpen,
  materialId,
  onClose,
  onSaved,
}: MaterialPropertiesPanelProps) {
  const authCtx = useContext(AuthContext)
  const canWrite = authCtx ? (authCtx.can('inventory.write') || authCtx.can('pr.write')) : true

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [detail, setDetail] = useState<MaterialStockPolicyDetailDto | null>(null)
  const [suppliers, setSuppliers] = useState<Supplier[]>([])

  // Form states
  const [minQty, setMinQty] = useState('')
  const [autoSuggest, setAutoSuggest] = useState(false)
  const [safetyStock, setSafetyStock] = useState('')
  const [reorderPoint, setReorderPoint] = useState('')
  const [maxQty, setMaxQty] = useState('')
  const [preferredSupplierId, setPreferredSupplierId] = useState('')
  const [leadTimeDays, setLeadTimeDays] = useState('')

  // UI state
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false)
  const [isDirty, setIsDirty] = useState(false)
  const [showUnsavedConfirm, setShowUnsavedConfirm] = useState(false)

  // Load data when opening
  const loadData = useCallback(async () => {
    if (!materialId) return
    setLoading(true)
    setError(null)
    setSaveSuccess(false)
    setIsDirty(false)
    try {
      const [data, supList] = await Promise.all([
        getMaterialStockPolicyApi(materialId),
        listSuppliersApi().catch(() => []),
      ])
      setDetail(data)
      setSuppliers(supList)

      // Initialize form from loaded policy
      setMinQty(data.policy.min_qty > 0 ? String(data.policy.min_qty) : '')
      setAutoSuggest(data.policy.replenish_policy === 'MIN_MAX' || data.policy.replenish_policy === 'REORDER_POINT')
      setSafetyStock(data.policy.safety_stock > 0 ? String(data.policy.safety_stock) : '')
      setReorderPoint(data.policy.reorder_point > 0 ? String(data.policy.reorder_point) : '')
      setMaxQty(data.policy.max_qty != null && data.policy.max_qty > 0 ? String(data.policy.max_qty) : '')
      setPreferredSupplierId(data.policy.preferred_supplier_id || '')
      setLeadTimeDays(data.policy.lead_time_days != null ? String(data.policy.lead_time_days) : '')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không tải được thuộc tính vật tư')
    } finally {
      setLoading(false)
    }
  }, [materialId])

  useEffect(() => {
    if (isOpen && materialId) {
      void loadData()
    } else {
      setDetail(null)
      setIsDirty(false)
      setShowUnsavedConfirm(false)
    }
  }, [isOpen, materialId, loadData])

  // Form validation
  const validation = useMemo(() => {
    return validateStockPolicyForm({
      minQty,
      autoSuggest,
      safetyStock,
      reorderPoint,
      maxQty,
      preferredSupplierId,
      leadTimeDays,
    })
  }, [minQty, autoSuggest, safetyStock, reorderPoint, maxQty, preferredSupplierId, leadTimeDays])

  function handleFieldChange(setter: (val: string) => void) {
    return (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      setter(e.target.value)
      setIsDirty(true)
      setSaveSuccess(false)
    }
  }

  function handleToggleAutoSuggest(checked: boolean) {
    setAutoSuggest(checked)
    setIsDirty(true)
    setSaveSuccess(false)
  }

  function handleAttemptClose() {
    if (isDirty) {
      setShowUnsavedConfirm(true)
    } else {
      onClose()
    }
  }

  async function handleSave() {
    if (!materialId || !canWrite || !validation.isValid) return
    setSaving(true)
    setError(null)
    setSaveSuccess(false)

    try {
      const minNum = minQty.trim() === '' ? 0 : Number(minQty)
      const safetyNum = safetyStock.trim() === '' ? 0 : Number(safetyStock)
      const ropNum = reorderPoint.trim() === '' ? 0 : Number(reorderPoint)
      const maxNum = maxQty.trim() === '' ? null : Number(maxQty)
      const leadNum = leadTimeDays.trim() === '' ? null : Number(leadTimeDays)

      let policy: ReplenishPolicy = 'MANUAL'
      if (autoSuggest) {
        policy = ropNum > 0 && detail?.policy.replenish_policy === 'REORDER_POINT' ? 'REORDER_POINT' : 'MIN_MAX'
      }

      await updateMaterialStockPolicyApi(materialId, {
        min_qty: minNum,
        max_qty: maxNum,
        safety_stock: safetyNum,
        reorder_point: ropNum,
        replenish_policy: policy,
        preferred_supplier_id: preferredSupplierId.trim() !== '' ? preferredSupplierId : null,
        lead_time_days: leadNum,
      })

      setIsDirty(false)
      setSaveSuccess(true)
      onSaved?.()
      // Reload updated detail to show fresh inventory threshold
      void loadData()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Lỗi khi lưu chính sách tồn kho')
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <Drawer
      open={isOpen}
      onClose={handleAttemptClose}
      title="Thuộc tính vật tư"
      widthClass="w-full max-w-xl"
    >
      <div className="flex flex-1 flex-col overflow-y-auto p-4 text-xs">
        {loading ? (
          <div className="py-16 text-center text-slate-400">Đang tải thông tin vật tư...</div>
        ) : error && !detail ? (
          <div className="rounded border border-red-200 bg-red-50 p-4 text-red-700">
            <p className="font-medium">{error}</p>
            <Button variant="secondary" className="mt-3 text-xs" onClick={() => void loadData()}>
              Thử lại
            </Button>
          </div>
        ) : detail ? (
          <div className="space-y-5">
            {/* 1. Header Information (NO MATERIAL CODE SHOWN TO USER) */}
            <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-3 shadow-xs">
              <h3 className="text-sm font-semibold text-[var(--color-text)] leading-snug">
                {detail.material_name}
              </h3>
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-slate-600">
                <div>
                  <span className="text-slate-400">Quy cách:</span>{' '}
                  <span className="font-medium text-slate-800">{detail.spec || '—'}</span>
                </div>
                <div>
                  <span className="text-slate-400">ĐVT gốc:</span>{' '}
                  <span className="font-medium text-slate-800">{detail.uom_default || '—'}</span>
                </div>
                <div>
                  <span className="text-slate-400">Phân loại:</span>{' '}
                  <span className="font-medium text-slate-800">
                    {detail.kind === 'RAW' ? 'Nguyên vật liệu chính (RAW)' : 'Vật tư phụ (AUX)'}
                  </span>
                </div>
              </div>
            </div>

            {/* Read-only permission alert */}
            {!canWrite && (
              <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-slate-600">
                <b>Chế độ xem:</b> Tài khoản của bạn không có quyền sửa thiết lập tồn kho (chỉ dành cho vai trò Kho và Mua hàng).
              </div>
            )}

            {/* Save alerts */}
            {error && (
              <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-red-700">
                {error}
              </div>
            )}
            {saveSuccess && (
              <div className="flex items-center gap-2 rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-800">
                <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
                <span>Đã lưu thiết lập thuộc tính vật tư thành công.</span>
              </div>
            )}

            {/* 2. Current Inventory (Read-Only) */}
            <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-3 shadow-xs">
              <div className="mb-2.5 flex items-center justify-between">
                <span className="font-semibold text-slate-800 uppercase tracking-wider text-[11px]">
                  Tồn kho hiện tại (Postgres SoT)
                </span>
                {detail.inventory.out_of_stock ? (
                  <StatusBadge tone="danger" label="Hết hàng" />
                ) : detail.inventory.below_threshold ? (
                  <StatusBadge tone="warning" label="Dưới ngưỡng" />
                ) : (
                  <StatusBadge tone="success" label="Ổn định" />
                )}
              </div>

              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <div className="rounded border border-slate-100 bg-slate-50 p-2 text-center">
                  <div className="text-[11px] text-slate-500">Tồn vật lý</div>
                  <div className="mt-0.5 text-sm font-semibold tabular-nums text-slate-800">
                    {detail.inventory.physical_available.toLocaleString('vi-VN')} {detail.uom_default}
                  </div>
                </div>

                <div className="rounded border border-slate-100 bg-slate-50 p-2 text-center">
                  <div className="text-[11px] text-slate-500">Đang giữ</div>
                  <div className={`mt-0.5 text-sm font-semibold tabular-nums ${detail.inventory.reserved_base > 0 ? 'text-amber-700' : 'text-slate-800'}`}>
                    {detail.inventory.reserved_base.toLocaleString('vi-VN')} {detail.uom_default}
                  </div>
                </div>

                <div className="rounded border border-emerald-100 bg-emerald-50/70 p-2 text-center">
                  <div className="text-[11px] text-emerald-800 font-medium">Khả dụng thực</div>
                  <div className="mt-0.5 text-sm font-bold tabular-nums text-emerald-900">
                    {detail.inventory.available.toLocaleString('vi-VN')} {detail.uom_default}
                  </div>
                </div>

                <div className="rounded border border-slate-100 bg-slate-50 p-2 text-center">
                  <div className="text-[11px] text-slate-500">Đang về (PO)</div>
                  <div className="mt-0.5 text-sm font-semibold tabular-nums text-slate-800">
                    {detail.inventory.incoming_base.toLocaleString('vi-VN')} {detail.uom_default}
                    {detail.inventory.incoming_unresolved ? ' *' : ''}
                  </div>
                </div>
              </div>

              {detail.inventory.incoming_unresolved && (
                <p className="mt-2 text-[11px] text-amber-700">
                  * Có PO chưa quy đổi được đơn vị (thiếu hệ số quy đổi).
                </p>
              )}
            </div>

            {/* 3. Warning Threshold & Auto Replenishment */}
            <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-3 shadow-xs space-y-3">
              <span className="font-semibold text-slate-800 uppercase tracking-wider text-[11px] block">
                Ngưỡng cảnh báo & Đề xuất mua
              </span>

              {/* Threshold input */}
              <div>
                <label htmlFor="input-min-qty" className="block text-xs font-medium text-slate-700">
                  Cảnh báo khi tồn dưới ({detail.uom_default || 'đơn vị gốc'})
                </label>
                <div className="mt-1 flex items-center gap-2">
                  <input
                    id="input-min-qty"
                    type="number"
                    min="0"
                    step="any"
                    disabled={!canWrite}
                    value={minQty}
                    onChange={handleFieldChange(setMinQty)}
                    placeholder="0 (không theo dõi)"
                    className={`w-full max-w-xs rounded border px-2.5 py-1.5 text-xs text-slate-800 shadow-2xs focus:border-[var(--color-accent)] focus:outline-hidden ${
                      validation.errors.minQty ? 'border-red-500 bg-red-50/50' : 'border-[var(--color-border)] bg-white'
                    } ${!canWrite ? 'bg-slate-100 cursor-not-allowed opacity-75' : ''}`}
                  />
                  <span className="text-slate-500 text-xs">{detail.uom_default}</span>
                </div>
                {validation.errors.minQty ? (
                  <p className="mt-1 text-[11px] text-red-600">{validation.errors.minQty}</p>
                ) : (
                  <p className="mt-1 text-[11px] text-slate-500">
                    Để trống hoặc 0 = không theo dõi cảnh báo cho vật tư này.
                  </p>
                )}
              </div>

              {/* Auto suggestion toggle */}
              <div className="rounded border border-slate-200 bg-slate-50 p-2.5">
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    disabled={!canWrite}
                    checked={autoSuggest}
                    onChange={(e) => handleToggleAutoSuggest(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[var(--color-accent)] focus:ring-1 focus:ring-[var(--color-accent)]"
                  />
                  <div>
                    <span className="font-medium text-slate-800 text-xs block">
                      Tự động đề xuất mua khi dưới ngưỡng
                    </span>
                    <span className="text-[11px] text-slate-500 block mt-0.5">
                      Bật = Hệ thống tự động tạo Đề xuất mua (Purchase Suggestion) trong mục Mua hàng khi tồn khả dụng xuống dưới ngưỡng cảnh báo.
                    </span>
                  </div>
                </label>
              </div>
            </div>

            {/* 4. Advanced Settings (Collapsible) */}
            <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] shadow-xs">
              <button
                type="button"
                onClick={() => setIsAdvancedOpen((v) => !v)}
                className="flex w-full items-center justify-between p-3 text-left font-semibold text-slate-800 hover:bg-slate-50"
              >
                <span className="uppercase tracking-wider text-[11px]">Thiết lập nâng cao</span>
                {isAdvancedOpen ? <ChevronUp className="h-4 w-4 text-slate-500" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
              </button>

              {isAdvancedOpen && (
                <div className="border-t border-[var(--color-border)] p-3 space-y-3 bg-slate-50/50">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                      <label htmlFor="input-safety-stock" className="block text-xs font-medium text-slate-700">
                        Tồn an toàn (Safety Stock)
                      </label>
                      <input
                        id="input-safety-stock"
                        type="number"
                        min="0"
                        step="any"
                        disabled={!canWrite}
                        value={safetyStock}
                        onChange={handleFieldChange(setSafetyStock)}
                        placeholder="0"
                        className="mt-1 w-full rounded border border-[var(--color-border)] bg-white px-2.5 py-1.5 text-xs text-slate-800 shadow-2xs focus:border-[var(--color-accent)] focus:outline-hidden"
                      />
                      {validation.errors.safetyStock && (
                        <p className="mt-1 text-[11px] text-red-600">{validation.errors.safetyStock}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="input-reorder-point" className="block text-xs font-medium text-slate-700">
                        Điểm đặt hàng lại (ROP)
                      </label>
                      <input
                        id="input-reorder-point"
                        type="number"
                        min="0"
                        step="any"
                        disabled={!canWrite}
                        value={reorderPoint}
                        onChange={handleFieldChange(setReorderPoint)}
                        placeholder="0"
                        className="mt-1 w-full rounded border border-[var(--color-border)] bg-white px-2.5 py-1.5 text-xs text-slate-800 shadow-2xs focus:border-[var(--color-accent)] focus:outline-hidden"
                      />
                      {validation.errors.reorderPoint && (
                        <p className="mt-1 text-[11px] text-red-600">{validation.errors.reorderPoint}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="input-max-qty" className="block text-xs font-medium text-slate-700">
                        Tồn tối đa (Max Qty)
                      </label>
                      <input
                        id="input-max-qty"
                        type="number"
                        min="0"
                        step="any"
                        disabled={!canWrite}
                        value={maxQty}
                        onChange={handleFieldChange(setMaxQty)}
                        placeholder="Không giới hạn"
                        className="mt-1 w-full rounded border border-[var(--color-border)] bg-white px-2.5 py-1.5 text-xs text-slate-800 shadow-2xs focus:border-[var(--color-accent)] focus:outline-hidden"
                      />
                      {validation.errors.maxQty && (
                        <p className="mt-1 text-[11px] text-red-600">{validation.errors.maxQty}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="input-lead-time" className="block text-xs font-medium text-slate-700">
                        Thời gian giao hàng (ngày)
                      </label>
                      <input
                        id="input-lead-time"
                        type="number"
                        min="0"
                        step="1"
                        disabled={!canWrite}
                        value={leadTimeDays}
                        onChange={handleFieldChange(setLeadTimeDays)}
                        placeholder="Số ngày giao"
                        className="mt-1 w-full rounded border border-[var(--color-border)] bg-white px-2.5 py-1.5 text-xs text-slate-800 shadow-2xs focus:border-[var(--color-accent)] focus:outline-hidden"
                      />
                      {validation.errors.leadTimeDays && (
                        <p className="mt-1 text-[11px] text-red-600">{validation.errors.leadTimeDays}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="select-preferred-supplier" className="block text-xs font-medium text-slate-700">
                      Nhà cung cấp ưu tiên
                    </label>
                    <select
                      id="select-preferred-supplier"
                      disabled={!canWrite}
                      value={preferredSupplierId}
                      onChange={handleFieldChange(setPreferredSupplierId)}
                      className="mt-1 w-full rounded border border-[var(--color-border)] bg-white px-2.5 py-1.5 text-xs text-slate-800 shadow-2xs focus:border-[var(--color-accent)] focus:outline-hidden"
                    >
                      <option value="">-- Chọn Nhà cung cấp --</option>
                      {suppliers.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {detail.policy.updated_at && (
                    <div className="pt-2 text-[11px] text-slate-400">
                      Cập nhật lần cuối: {new Date(detail.policy.updated_at).toLocaleString('vi-VN')}
                      {detail.policy.updated_by ? ` bởi ${detail.policy.updated_by}` : ''}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* 5. Recent Purchases (Read-Only) */}
            <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-3 shadow-xs">
              <div className="mb-2 flex items-center justify-between">
                <span className="font-semibold text-slate-800 uppercase tracking-wider text-[11px]">
                  Giá mua gần nhất (từ PO)
                </span>
              </div>

              {detail.recent_purchases.length === 0 ? (
                <p className="py-2 text-[11px] text-slate-400 italic">
                  Chưa có lịch sử đơn đặt hàng (PO) cho vật tư này.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-[11px]">
                    <thead>
                      <tr className="border-b border-slate-200 text-slate-400">
                        <th className="py-1 pr-2 font-medium">STT</th>
                        <th className="py-1 pr-2 font-medium">Mã PO</th>
                        <th className="py-1 pr-2 font-medium">Nhà cung cấp</th>
                        <th className="py-1 pr-2 text-right font-medium">Đơn giá</th>
                        <th className="py-1 pr-2 font-medium">ĐVT</th>
                        <th className="py-1 text-right font-medium">Ngày đặt</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {detail.recent_purchases.map((p, idx) => (
                        <tr key={p.po_id + '-' + idx} className="hover:bg-slate-50">
                          <td className="py-1.5 pr-2 tabular-nums text-slate-400">{idx + 1}</td>
                          <td className="py-1.5 pr-2 font-mono font-medium text-slate-800">
                            {p.po_no}
                          </td>
                          <td className="py-1.5 pr-2 text-slate-700 truncate max-w-[140px]" title={p.supplier_name}>
                            {p.supplier_name}
                          </td>
                          <td className="py-1.5 pr-2 text-right font-semibold tabular-nums text-slate-800">
                            {p.unit_price_actual.toLocaleString('vi-VN')} đ
                          </td>
                          <td className="py-1.5 pr-2 text-slate-600">{p.price_uom}</td>
                          <td className="py-1.5 text-right tabular-nums text-slate-500">
                            {formatDate(p.created_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* 6. Shortcuts */}
            <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-3 shadow-xs space-y-2">
              <span className="font-semibold text-slate-800 uppercase tracking-wider text-[11px] block">
                Lối tắt nghiệp vụ
              </span>
              <div className="flex flex-wrap gap-2 text-xs">
                <Link
                  to={`/warehouse/stock/${detail.material_id}`}
                  className="inline-flex items-center gap-1 rounded border border-slate-200 bg-white px-2.5 py-1 text-slate-700 hover:border-[var(--color-accent)] hover:text-slate-900"
                >
                  <TrendingDown className="h-3 w-3 text-slate-500" />
                  <span>Chi tiết thẻ kho</span>
                </Link>
                <Link
                  to={`/warehouse/transactions?material_id=${detail.material_id}`}
                  className="inline-flex items-center gap-1 rounded border border-slate-200 bg-white px-2.5 py-1 text-slate-700 hover:border-[var(--color-accent)] hover:text-slate-900"
                >
                  <History className="h-3 w-3 text-slate-500" />
                  <span>Biến động kho</span>
                </Link>
                <Link
                  to="/purchasing/orders"
                  className="inline-flex items-center gap-1 rounded border border-slate-200 bg-white px-2.5 py-1 text-slate-700 hover:border-[var(--color-accent)] hover:text-slate-900"
                >
                  <Truck className="h-3 w-3 text-slate-500" />
                  <span>Đơn mua (PO) đang về</span>
                </Link>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {/* Unsaved changes confirmation modal */}
      {showUnsavedConfirm && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
          <div className="w-full max-w-sm rounded-lg border border-slate-200 bg-white p-4 shadow-xl">
            <div className="flex items-center gap-2 text-amber-600 font-semibold text-sm">
              <AlertTriangle className="h-4 w-4" />
              <span>Thay đổi chưa được lưu</span>
            </div>
            <p className="mt-2 text-xs text-slate-600">
              Bạn có một số thay đổi trong thuộc tính tồn kho chưa lưu. Bạn có chắc muốn đóng và hủy các thay đổi này?
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <Button
                variant="secondary"
                className="text-xs"
                onClick={() => setShowUnsavedConfirm(false)}
              >
                Tiếp tục sửa
              </Button>
              <Button
                variant="primary"
                className="text-xs bg-red-600 hover:bg-red-700"
                onClick={() => {
                  setShowUnsavedConfirm(false)
                  setIsDirty(false)
                  onClose()
                }}
              >
                Hủy thay đổi & Đóng
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Footer Actions */}
      <div className="flex items-center justify-between border-t border-[var(--color-border)] bg-slate-50 px-4 py-3">
        <div className="text-[11px] text-slate-500">
          {isDirty ? (
            <span className="text-amber-700 font-medium">● Có thay đổi chưa lưu</span>
          ) : (
            <span>Không có thay đổi</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" className="text-xs" onClick={handleAttemptClose}>
            Đóng
          </Button>
          {canWrite && (
            <Button
              variant="primary"
              className="text-xs"
              disabled={saving || !validation.isValid || !isDirty}
              onClick={() => void handleSave()}
            >
              {saving ? 'Đang lưu...' : 'Lưu thiết lập'}
            </Button>
          )}
        </div>
      </div>
    </Drawer>
  )
}
